import { MSpinner, MSplitter } from "components/Shared";
import { ClockInOutCard, QualityAssuranceAuditResults } from "components/Shared/TimeLogs";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  clockIn,
  clockOut,
  getActiveClock,
  getTimeClockByPayPeriod
} from "actions/account";
import { EmployeeTimeClockActiveDto, PayPeriodViewModel, QATimeByDaySummary, QaTimeCardResponseDetail } from "interfaces/Mueller.Business.Models";
import { getPayPeriodList } from "actions/casetimelog";
import { PayPeriodDropdown } from "components/User";
import { appendHeader, clearHeader } from "actions/layout";
import { HeaderDefinition } from "typeConstants/header/headerDefinitions";
import { getTimeLogSummaryForCurrentUser } from "requests/qualityAssurance";
import responseHandler from "responseHandler";
import TimeLogSummaryGrid from "./TimeLogSummary";
import SummaryCardBusOps from "components/Shared/TimeLogs/SummaryCardBusOps";
import { getTimeLogByDaySummary } from "actions/qualityAssurance";
import QAPerformanceLog from "../QAPerformanceLog";
import QAPerformanceSpeedometers from "../QAPerformanceSpeedometers";

interface QADashboardProps {
  //Redux state
  timeClockByPayPeriod: QATimeByDaySummary,
  activeClock: EmployeeTimeClockActiveDto,
  isFetchingTimeClockByPayPeriod: boolean,
  payPeriods: PayPeriodViewModel[],
  isFetchingActiveClock: boolean,
  updatingUser: boolean,
  isFetchingPayPeriods: boolean,
  isFetchingTimeLogByDaySummary: boolean,
  timeLogByDaySummary: QATimeByDaySummary,
  internetUserId: number,
  //Dispatch
  getActiveClock: (timeZoneOffset: number) => void,
  getTimeClockByPayPeriod: (internetUserId: number, payPeriodId: number) => void,
  clockIn: (timeZoneOffset: number) => void,
  clockOut: (timeZoneOffset: number) => void,
  getPayPeriodList: () => void,
  clearHeader: () => void,
  appendHeader: (headerDefintions: HeaderDefinition) => void,
  makeErrorToast: (text: string) => void,
  getTimeLogByDaySummary: (internetUserId: number, payPeriodId: number) => void,
}

const QADashboardContainer = (props: QADashboardProps) => {

  const [selectedPayPeriodIndex, setSelectedPayPeriodIndex] = useState<number>(0);
  const [timeLogSummary, setTimeLogSummary] = useState<QaTimeCardResponseDetail>();
  const [isDisplayingSecondPane, setIsDisplayingSecondPane] = useState<boolean>(false);

  const mounted = useRef<boolean>(true);

  const payPeriodsSuccessfullyFetched = props.payPeriods && props.payPeriods.length;
  const timeLogSummarySuccessfullyFetched = timeLogSummary && Object.keys(timeLogSummary).length > 0;

  useEffect(() => {
    props.getPayPeriodList();
    return () => { 
      props.clearHeader();
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (selectedPayPeriodIndex !== null && selectedPayPeriodIndex !== undefined && payPeriodsSuccessfullyFetched) {
      getTimeLogSummaryForCurrentUser(
        props.payPeriods[selectedPayPeriodIndex].id,
        mounted,
        (response: QaTimeCardResponseDetail) => {
          setTimeLogSummary(response);
        },
        () => {
          props.makeErrorToast("Failed to fetch time log summary for the selected pay period");
        }
      );
    }
  }, [selectedPayPeriodIndex, props.payPeriods, props.activeClock]);

  if (!payPeriodsSuccessfullyFetched || !timeLogSummarySuccessfullyFetched) {
    return <MSpinner />;
  }

  const selectedPayPeriod = props.payPeriods[selectedPayPeriodIndex];

  return (
    <MSplitter
      className="qa-dashboard"
      firstChild={
        <>
          {props.payPeriods &&
            <PayPeriodDropdown
              payPeriods={props.payPeriods}
              payPeriodChangeCallback={setSelectedPayPeriodIndex}
            />}
          <div style={{ flexShrink: 0 }}>
            <QAPerformanceSpeedometers internetUserId={props.internetUserId} />
          </div>
          <div style={{ flexShrink: 0 }}>
            <ClockInOutCard
              activeClock={props.activeClock}
              displayClockInOutButton={true}
              data={props.timeClockByPayPeriod}
              isFetchingData={props.isFetchingTimeClockByPayPeriod}
              payPeriod={selectedPayPeriod}
              getData={props.getTimeClockByPayPeriod}
              getActiveClock={props.getActiveClock}
              isFetchingActiveClock={props.isFetchingActiveClock}
              clockIn={props.clockIn}
              clockOut={props.clockOut}
              updatingUser={props.updatingUser}
            />
          </div>
          <div id="qa-dashboard-time-log-summary-card">
            <TimeLogSummaryGrid
              summaryData={timeLogSummary}
              onCellButtonClick={() => { setIsDisplayingSecondPane(!isDisplayingSecondPane); }}
            />
          </div>
        </>
      }
      secondChild={isDisplayingSecondPane &&
        <div className="qa-dashboard-second-child">
          <SummaryCardBusOps
            isBusOpsEmployeeTimeLogs={true}
            data={props.timeLogByDaySummary}
            isFetchingData={props.isFetchingTimeLogByDaySummary}
            payPeriodId={selectedPayPeriod.id}
            getData={props.getTimeLogByDaySummary}
            internetUserId={props.internetUserId}
          />
          <QualityAssuranceAuditResults
            internetUserId={props.internetUserId}
          />
          <QAPerformanceLog
            internetUserId={props.internetUserId}
            payPeriodId={selectedPayPeriod.id}
          />
        </div>
      }
      secondChildTitle={{ 
        title: "Time Log",
        buttons: [
          {
            icon: "close",
            onClick: () => setIsDisplayingSecondPane(false),
          }
        ]
      }}
    />
  );
};

const mapStateToProps = state => {
  return {
    updatingUser: state.user.updatingUser,
    isFetchingActiveClock: state.user.isFetchingActiveClock,
    activeClock: state.user.activeClock,
    isFetchingTimeLogs: state.caseTimeLog.isFetchingTimeLogs,
    timeLogs: state.caseTimeLog.timeLogs,
    isFetchingPayPeriods: state.caseTimeLog.isFetchingPayPeriods,
    payPeriods: state.caseTimeLog.payPeriods,
    timeClockByPayPeriod: state.user.timeClockByPayPeriod,
    isFetchingTimeClockByPayPeriod: state.user.isFetchingTimeClockByPayPeriod,
    timeLogByDaySummary: state.qualityAssurance.timeLogByDaySummary,
    isFetchingTimeLogByDaySummary: state.qualityAssurance.isFetchingTimeLogByDaySummary,
    internetUserId: state.user.profile.internetUserId,
  };
};

const mapDispatchToProps = dispatch => ({
  getActiveClock(timeZoneOffset: number) {
    dispatch(getActiveClock(timeZoneOffset));
  },
  clockIn(timeZoneOffset: number) {
    dispatch(clockIn(timeZoneOffset));
  },
  clockOut(timeZoneOffset: number) {
    dispatch(clockOut(timeZoneOffset));
  },
  getPayPeriodList() {
    dispatch(getPayPeriodList());
  },
  getTimeClockByPayPeriod(internetUserId: number, payPeriodId: number) {
    dispatch(getTimeClockByPayPeriod(internetUserId, payPeriodId));
  },
  appendHeader(headerDefintions: HeaderDefinition) {
    dispatch(appendHeader(headerDefintions));
  },
  clearHeader() {
    dispatch(clearHeader());
  },
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
  getTimeLogByDaySummary(internetUserId: number, payPeriodId: number) {
    dispatch(getTimeLogByDaySummary(internetUserId, payPeriodId));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(QADashboardContainer);